import React, { useRef, useEffect, useState } from "react";
import { Tooltip } from "primereact/tooltip";

interface TruncatedTextProps {
  text: string;
  /**
   * Optional full value for tooltip.
   * Useful when the displayed text is intentionally truncated (e.g. by word count).
   */
  tooltipText?: string;
  className?: string;
  tooltipPosition?: "top" | "right" | "bottom" | "left";
  maxWidth?: string;
  tooltipWidth?: string;
  tooltipId?: string;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  tooltipText,
  className = "",
  tooltipPosition = "top",
  maxWidth = "max-w-full",
  tooltipWidth = "max-w-md",
  tooltipId = "tooltip",
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const computeTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    // Initial check + one animation frame later (layout/column widths may settle after mount).
    computeTruncation();
    const rafId = window.requestAnimationFrame(computeTruncation);

    // PrimeReact tables often adjust column widths after render; observe size changes for accuracy.
    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => computeTruncation());
      resizeObserver.observe(element);
      return () => {
        window.cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
      };
    }

    // Fallback when ResizeObserver is not available.
    window.addEventListener("resize", computeTruncation);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", computeTruncation);
    };
  }, [text, className, maxWidth]);

  // Clean tooltipId to ensure valid CSS selector
  const cleanTooltipId = tooltipId.replace(/[^a-zA-Z0-9-_]/g, "");
  const tooltipValue = tooltipText ?? text;
  const shouldShowTooltip = tooltipValue !== text || isTruncated;

  return (
    <>
      <div
        ref={elementRef}
        className={`truncate ${maxWidth} ${className} tooltip-${cleanTooltipId}`}
        data-pr-tooltip={shouldShowTooltip ? tooltipValue : undefined}
        data-pr-position={tooltipPosition}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {text}
      </div>
      {shouldShowTooltip && (
        <Tooltip
          target={`.tooltip-${cleanTooltipId}`}
          className={`${tooltipWidth}`}
          style={{
            fontSize: "0.875rem",
            padding: "0.5rem 0.75rem",
            color: "#fff",
            borderRadius: "0.375rem",
            zIndex: 9999,
          }}
        />
      )}
    </>
  );
};

export default TruncatedText;
